/**
 * Adds the item number (itemid, e.g. "CC 01386") under the description of
 * every type-ahead suggestion. When the shopper's query matched the item's
 * Old Part Number instead — exactly, or as a prefix of a part-number-like
 * value — that number is shown after it ("CC 01386 · CSD01386") so they can
 * see why the item came up.
 *
 * How: every type-ahead response (fieldset=typeahead) already carries itemid,
 * urlcomponent and custitem_old_part_number per item. An ajaxPrefilter
 * records "/<urlcomponent>" -> { item, query } from each response. A
 * MutationObserver on the type-ahead menu then appends the SKU line after
 * each suggestion's title whenever the menu (re)renders — the suggestion
 * anchor's data-hashtag is exactly "/<urlcomponent>".
 *
 * Why render-driven and not response-driven: SuiteCommerce's
 * Backbone.CachedSync serves a repeated identical query from an in-memory
 * cache without touching jQuery.ajax, so a response hook alone would miss
 * every repeat. The map survives for the page's lifetime.
 *
 * The query is read from the search input, falling back to the query recorded
 * with the response (which covers a cached render). The input is found by
 * taking the longest non-empty type-ahead value rather than by :visible —
 * the header search inputs can report zero size while the overlay animates,
 * and a :visible test then finds nothing.
 *
 * Why not override itemssearcher_item.tpl: an extension cannot override a
 * theme template by manifest, and the Manor copy of that template carries the
 * inline "Products / Categories" script that must stay intact. Why not patch
 * ItemsSearcher.Item.View: it is not a public core module, so Safe Mode
 * warns (and refuses the collection classes outright).
 */
define('Clemtex.Search.TypeaheadSku', [
    'Clemtex.Search.Utils',
    'jQuery',
    'underscore'
], function (Utils, jQuery, _) {
    'use strict';

    var SKU_CLASS = 'itemssearcher-item-results-sku';
    var ALT_CLASS = 'itemssearcher-item-results-sku-alt';
    var MAX_CACHE = 500;
    var entryByHashtag = {};
    var installed = false;
    var decoratePending = false;

    function remember(items, query) {
        if (_.size(entryByHashtag) > MAX_CACHE) { entryByHashtag = {}; }
        _.each(items, function (item) {
            if (item && item.urlcomponent && item.itemid) {
                entryByHashtag['/' + item.urlcomponent] = { item: item, query: query };
            }
        });
    }

    // What the shopper has typed, normalized. The header renders two inputs
    // (typeahead hint + real input) per searcher; the one with the most
    // characters is the one being typed into.
    function inputQuery() {
        var best = '';
        jQuery('input.itemssearcher-input.tt-input').each(function () {
            var value = String(this.value || '');
            if (value.length > best.length) { best = value; }
        });
        return Utils.normalize(best);
    }

    function skuLine(item, normalizedQuery) {
        var $line = jQuery('<div class="' + SKU_CLASS + '"></div>').text(item.itemid);
        var alt = Utils.matchedAltNumber(item, normalizedQuery);
        if (alt && Utils.normalize(alt) !== Utils.normalize(item.itemid)) {
            $line.append(document.createTextNode(' · '))
                 .append(jQuery('<span class="' + ALT_CLASS + '"></span>').text(alt));
        }
        return $line;
    }

    function decorate() {
        var typed = inputQuery();
        jQuery('a.itemssearcher-item-results').each(function () {
            var $anchor = jQuery(this);
            if ($anchor.find('.' + SKU_CLASS).length) { return; }
            var entry = entryByHashtag[$anchor.attr('data-hashtag')];
            var $title = $anchor.find('.itemssearcher-item-results-title');
            if (entry && $title.length) {
                $title.after(skuLine(entry.item, typed || Utils.normalize(entry.query)));
            }
        });
    }

    // Coalesces bursts of DOM mutations into one pass on the next tick.
    function scheduleDecorate() {
        if (decoratePending) { return; }
        decoratePending = true;
        setTimeout(function () {
            decoratePending = false;
            try { decorate(); } catch (e) {
                if (window.console) { window.console.error('ClemtexSearch TypeaheadSku', e); }
            }
        }, 0);
    }

    function isTypeaheadNode(node) {
        return !!(node && node.nodeType === 1 && node.closest && node.closest('.tt-menu'));
    }

    function observeMenu() {
        if (!window.MutationObserver || !document.body) { return; }
        var observer = new window.MutationObserver(function (mutations) {
            for (var i = 0; i < mutations.length; i++) {
                if (isTypeaheadNode(mutations[i].target)) {
                    scheduleDecorate();
                    return;
                }
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    return {
        decorate: decorate,
        skuLine: skuLine,
        inputQuery: inputQuery,

        mountToApp: function mountToApp() {
            if (installed) { return; }
            installed = true;

            jQuery.ajaxPrefilter(function (options, originalOptions, jqXHR) {
                if (!Utils.isItemsApi(options.url)) { return; }
                var params = Utils.requestParams(options);
                if (params.fieldset !== 'typeahead') { return; }
                jqXHR.done(function (json) {
                    remember(json && json.items, params.q);
                    scheduleDecorate();
                });
            });

            if (document.body) {
                observeMenu();
            } else {
                jQuery(observeMenu);
            }
        }
    };
});
